import SoyuzAnalytics from '@spotahome/soyuz-analytics';

// GA4 tracking for the conversational (agentic) search experience.
// Pure emitters over SoyuzAnalytics.sendGA4Event (a synchronous
// window.dataLayer.push under the hood — same rail as homecards-tracking).
//
// Two cross-cutting conventions hold the conversation funnel together:
//   - `conversation_id` (the chat sessionId) + `turn_index` ride on every
//     in-chat event so a whole session can be reconstructed end-to-end.
//   - new conversational events use the `agentic_*` snake_case prefix;
//     commerce events (view_item_list, select_item, add_to_wishlist) are
//     reused as-is elsewhere, tagged item_list_id: 'agentic_search'.
//
// Payload keys are snake_case to match the existing GA4 dimensions
// (item_list_id, metric_id, …). Optional values are dropped when absent
// so the dataLayer stays free of undefined noise.

// item_list_id used to tag the reused commerce events (view_item_list,
// select_item, add_to_wishlist) so the agentic path shows up in the
// existing conversion funnels alongside classic search.
export const AGENTIC_LIST_ID = 'agentic_search';

const compact = attrs =>
  Object.keys(attrs).reduce((acc, key) => {
    const value = attrs[key];
    if (value !== undefined && value !== null) acc[key] = value;
    return acc;
  }, {});

const send = (eventName, attrs) =>
  SoyuzAnalytics.sendGA4Event(eventName, compact(attrs));

// Homepage hero (top of funnel). No conversation_id yet — there is no
// session until the agentic page bootstraps. `source` separates raw
// free-text intent from a guided popular-city chip.
export const trackHomeSubmit = ({ source, city, charLength } = {}) =>
  send('agentic_home_submit', {
    source,
    city,
    char_length: charLength
  });

// First user message of a session. `entry_point`: seed (?q= from the
// hero), switcher (came from classic), or direct. `replaced_existing`
// flags the seed-on-top-of-existing-session reset.
export const trackChatStarted = ({
  conversationId,
  entryPoint,
  replacedExisting
} = {}) =>
  send('agentic_chat_started', {
    conversation_id: conversationId,
    entry_point: entryPoint,
    replaced_existing: replacedExisting
  });

// Every user message. `source`: typed, seed, or chip_removal (a filter
// chip × sends a natural-language removal message).
export const trackMessageSent = ({
  conversationId,
  turnIndex,
  charLength,
  source
} = {}) =>
  send('agentic_message_sent', {
    conversation_id: conversationId,
    turn_index: turnIndex,
    char_length: charLength,
    source
  });

// Assistant finished streaming a turn.
export const trackResponseReceived = ({
  conversationId,
  turnIndex,
  hadListings,
  resultCount,
  latencyMs
} = {}) =>
  send('agentic_response_received', {
    conversation_id: conversationId,
    turn_index: turnIndex,
    had_listings: hadListings,
    result_count: resultCount,
    latency_ms: latencyMs
  });

// Time-to-first-response: the gap between the user sending a message and the
// agent's first streamed chunk landing (the `submitted → streaming` edge).
// `latency_ms` here is perceived responsiveness — how long the "Thinking…"
// bubble shows before anything appears — distinct from
// agentic_response_received's `latency_ms`, which times the full turn.
export const trackFirstResponse = ({
  conversationId,
  turnIndex,
  latencyMs
} = {}) =>
  send('agentic_first_response', {
    conversation_id: conversationId,
    turn_index: turnIndex,
    latency_ms: latencyMs
  });

// A search turn resolved to zero cards — agent-quality signal.
export const trackNoResults = ({ conversationId, turnIndex } = {}) =>
  send('agentic_no_results', {
    conversation_id: conversationId,
    turn_index: turnIndex
  });

// `error_type`: session_expired (401), rate_limit (429), or internal.
export const trackResponseError = ({
  conversationId,
  turnIndex,
  errorType
} = {}) =>
  send('agentic_response_error', {
    conversation_id: conversationId,
    turn_index: turnIndex,
    error_type: errorType
  });

// Classic ↔ agentic toggle. Direction is implied by `context` (the
// originating surface), since the switcher's active side is a no-op.
// `message_count` is only meaningful when context === 'agentic'.
export const trackModeSwitch = ({ context, messageCount } = {}) =>
  send('search_mode_switch', {
    context,
    message_count: messageCount
  });

// User removed a filter chip the agent inferred — prompt-tuning signal.
export const trackFilterChipRemoved = ({
  conversationId,
  turnIndex,
  filterType
} = {}) =>
  send('agentic_filter_chip_removed', {
    conversation_id: conversationId,
    turn_index: turnIndex,
    filter_type: filterType
  });

// Mobile "Show on map" opened the bottom sheet.
export const trackShowOnMap = ({ conversationId, resultCount } = {}) =>
  send('agentic_show_on_map', {
    conversation_id: conversationId,
    result_count: resultCount
  });

export const trackMapMarkerClick = ({
  conversationId,
  listingId,
  position,
  resultCount
} = {}) =>
  send('agentic_map_marker_click', {
    conversation_id: conversationId,
    listing_id: listingId,
    position,
    result_count: resultCount
  });

export const trackLocateOnMap = ({
  conversationId,
  listingId,
  position
} = {}) =>
  send('agentic_locate_on_map', {
    conversation_id: conversationId,
    listing_id: listingId,
    position
  });
